

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import API_BASE_URL from '../../api';
import useIsMobile from '../../hooks/useIsMobile';
import { getLoanCollectedAmount, getLoanBalance } from '../../utils/loanUtils';
import CloseLoanForm from './CloseLoanForm';

const emptyNewLoanForm = {
	customer_id: '',
	loan_type: '',
	issue_date: new Date().toISOString().slice(0, 10),
	issue_amount: '',
	maturity_date: '',
};

const columns = [
	{ label: 'Loan ID', key: 'loan_id' },
	{ label: 'Customer ID', key: 'customer_id' },
	{ label: 'Customer Name', key: 'customer_name' },
	{ label: 'Loan Type', key: 'loan_type' },
	{ label: 'Loan Issue Date', key: 'issue_date' },
	{ label: 'Maturity Date', key: 'maturity_date' },
	{ label: 'Closing Date', key: 'closing_date' },
	{ label: 'Loan Issued Amount', key: 'issue_amount' },
	{ label: 'Collected Amount', key: 'collected_amount' },
	{ label: 'Balance', key: 'balance' },
	{ label: 'Adjustments', key: 'adjustments' },
	{ label: 'Status', key: 'status' },
	{ label: 'Re-Pledge Status', key: 'repledge_status' },
];

function formatDate(dateStr) {
	if (!dateStr) return '';
	const date = new Date(dateStr.split('T')[0]);
	if (isNaN(date)) return dateStr;
	const day = String(date.getDate()).padStart(2, '0');
	const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
	const year = date.getFullYear();
	return `${day}${month}${year}`;
}

function formatLoanType(loanType) {
	return String(loanType || '').replace(/\s*loan\s*/i, '').trim();
}

function formatAmount(value) {
	return (parseFloat(value) || 0).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

function toAmount(value) {
	const number = Number(value);
	return Number.isFinite(number) ? number : 0;
}

function addDaysUtc(dateObj, days) {
	const date = new Date(dateObj);
	date.setUTCDate(date.getUTCDate() + days);
	return date;
}

function addMonthsUtc(dateObj, months) {
	const date = new Date(dateObj);
	date.setUTCMonth(date.getUTCMonth() + months);
	return date;
}

function resolveLoanTypeCode(loanType = '') {
	const type = String(loanType).toLowerCase();
	if (type.includes('personal') || type === 'pl') return 'PL';
	if (type.includes('vehicle') || type === 'vl') return 'VL';
	if (type.includes('gold') || type === 'gl') return 'GL';
	return null;
}

function calculateDefaultMaturityDate(loanType, issueDate) {
	if (!loanType || !issueDate) return '';
	const issueDateObj = new Date(issueDate);
	if (Number.isNaN(issueDateObj.getTime())) return '';

	const loanTypeCode = resolveLoanTypeCode(loanType);
	if (loanTypeCode === 'PL') return addDaysUtc(issueDateObj, 100).toISOString().slice(0, 10);
	if (loanTypeCode === 'VL') return addDaysUtc(issueDateObj, 365).toISOString().slice(0, 10);
	if (loanTypeCode === 'GL') return addMonthsUtc(issueDateObj, 3).toISOString().slice(0, 10);
	return '';
}

function isOpenLoan(loan) {
	return String(loan?.status || '').toLowerCase() !== 'closed' && loan?.loan_status_closed !== true;
}

function isGoldLoan(loan) {
	return resolveLoanTypeCode(loan?.loan_type) === 'GL';
}

function getRepledgeStatus(loan) {
	if (!isGoldLoan(loan)) return '—';
	return loan.repledge_status || 'Not Pledged';
}

const repledgeBadgeColors = {
	'Not Pledged': { background: '#eef2f6', color: '#475467' },
	Pledged: { background: '#dcfce7', color: '#166534' },
	Overdue: { background: '#fee2e2', color: '#b42318' },
	Released: { background: '#dbeafe', color: '#1d4ed8' },
};

function RepledgeBadge({ loan }) {
	const status = getRepledgeStatus(loan);
	if (status === '—') return status;
	const colors = repledgeBadgeColors[status] || repledgeBadgeColors['Not Pledged'];
	return <span style={{ ...colors, display: 'inline-block', padding: '3px 8px', borderRadius: 999, fontWeight: 700, whiteSpace: 'nowrap' }}>{status}</span>;
}

const Loans = () => {
	const isMobile = useIsMobile();
	const [loanView, setLoanView] = useState('all'); // 'all', 'open', 'closed'
	const [repledgeFilter, setRepledgeFilter] = useState('all');

	const toggleLoanView = () => {
		setLoanView(prev => prev === 'all' ? 'open' : prev === 'open' ? 'closed' : 'all');
	};
	const [sortKey, setSortKey] = useState('');
	const [sortOrder, setSortOrder] = useState('asc');

	// Sort handler
	const handleSort = (key) => {
		if (sortKey === key) {
			setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
		} else {
			setSortKey(key);
			setSortOrder('asc');
		}
	};
	const [loans, setLoans] = useState([]);
	const [filter, setFilter] = useState('');
	const [loading, setLoading] = useState(true);
	const [showNewLoanModal, setShowNewLoanModal] = useState(false);
	const [newLoanForm, setNewLoanForm] = useState(emptyNewLoanForm);
	const [customers, setCustomers] = useState([]);
	const [loanTypes, setLoanTypes] = useState([]);
	const [newLoanLoading, setNewLoanLoading] = useState(false);
	const [newLoanSubmitting, setNewLoanSubmitting] = useState(false);
	const [newLoanError, setNewLoanError] = useState('');
	const [showCloseLoanModal, setShowCloseLoanModal] = useState(false);
	const [selectedLoan, setSelectedLoan] = useState(null);
	const loansRequestRef = useRef(null);

	const loadLoans = useCallback(() => {
		loansRequestRef.current?.abort();
		const controller = new AbortController();
		loansRequestRef.current = controller;
		setLoading(true);
		const params = new URLSearchParams();
		if (isMobile) {
			params.set('limit', '100');
			if (filter.trim()) params.set('text', filter.trim());
		}
		fetch(`${API_BASE_URL}/api/loans${params.toString() ? `?${params}` : ''}`, { signal: controller.signal })
			.then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
			.then(data => {
				setLoans(Array.isArray(data) ? data : []);
				setSelectedLoan(current => {
					if (!current) return null;
					const refreshedLoan = Array.isArray(data) ? data.find(loan => loan.loan_id === current.loan_id) : null;
					return refreshedLoan || null;
				});
				setLoading(false);
			})
			.catch(error => {
				if (error.name === 'AbortError') return;
				setLoans([]);
				setLoading(false);
			});
	}, [filter, isMobile]);

	useEffect(() => {
		loadLoans();
		return () => loansRequestRef.current?.abort();
	}, [loadLoans]);

	useEffect(() => {
		if (!showNewLoanModal) return;
		setNewLoanLoading(true);
		setNewLoanError('');
		Promise.all([
			fetch(`${API_BASE_URL}/api/customers`).then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))),
			fetch(`${API_BASE_URL}/api/loan-types`).then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))),
		])
			.then(([customerData, loanTypeData]) => {
				setCustomers(Array.isArray(customerData) ? customerData : []);
				setLoanTypes(Array.isArray(loanTypeData) ? loanTypeData : []);
			})
			.catch(() => {
				setCustomers([]);
				setLoanTypes([]);
				setNewLoanError('Unable to load customers or loan types.');
			})
			.finally(() => setNewLoanLoading(false));
	}, [showNewLoanModal]);

	useEffect(() => {
		if (!showNewLoanModal) return;
		const maturityDate = calculateDefaultMaturityDate(newLoanForm.loan_type, newLoanForm.issue_date);
		setNewLoanForm(current => (
			current.maturity_date === maturityDate
				? current
				: { ...current, maturity_date: maturityDate }
		));
	}, [showNewLoanModal, newLoanForm.loan_type, newLoanForm.issue_date]);

	const openNewLoanModal = () => {
		setNewLoanForm({
			...emptyNewLoanForm,
			issue_date: new Date().toISOString().slice(0, 10),
		});
		setNewLoanError('');
		setShowNewLoanModal(true);
	};

	const closeNewLoanModal = () => {
		if (newLoanSubmitting) return;
		setShowNewLoanModal(false);
		setNewLoanForm(emptyNewLoanForm);
		setNewLoanError('');
	};

	const handleNewLoanChange = (e) => {
		const { name, value } = e.target;
		setNewLoanForm(current => ({
			...current,
			[name]: value,
		}));
	};

	const handleNewLoanSubmit = async (e) => {
		e.preventDefault();
		setNewLoanError('');

		if (!newLoanForm.customer_id || !newLoanForm.loan_type || !newLoanForm.issue_date || !newLoanForm.issue_amount) {
			setNewLoanError('Customer, loan type, issue date, and amount are required.');
			return;
		}

		setNewLoanSubmitting(true);
		try {
			const res = await fetch(`${API_BASE_URL}/api/loans`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(newLoanForm),
			});
			const data = await res.json().catch(() => ({}));
			if (!res.ok) {
				throw new Error(data.error || 'Failed to add loan.');
			}

			setShowNewLoanModal(false);
			setNewLoanForm(emptyNewLoanForm);
			loadLoans();
		} catch (err) {
			setNewLoanError(err.message || 'Failed to add loan.');
		} finally {
			setNewLoanSubmitting(false);
		}
	};

	const filteredLoans = useMemo(() => {
		let filtered = loans.filter(loan =>
			Object.values(loan).some(val =>
				String(val).toLowerCase().includes(filter.toLowerCase())
			)
		);
		if (loanView === 'open') {
			filtered = filtered.filter(isOpenLoan);
		} else if (loanView === 'closed') {
			filtered = filtered.filter(loan => !isOpenLoan(loan));
		}
		if (repledgeFilter !== 'all') {
			filtered = filtered.filter(loan => isGoldLoan(loan) && getRepledgeStatus(loan) === repledgeFilter);
		}
		if (!sortKey) return filtered;
		const sorted = [...filtered].sort((a, b) => {
			let aVal = a[sortKey];
			let bVal = b[sortKey];
			// Special handling for collected_amount and balance
			if (sortKey === 'collected_amount') {
				aVal = getLoanCollectedAmount(a);
				bVal = getLoanCollectedAmount(b);
			} else if (sortKey === 'balance') {
				aVal = getLoanBalance(a);
				bVal = getLoanBalance(b);
			}
			// Try numeric sort if possible
			if (!isNaN(parseFloat(aVal)) && !isNaN(parseFloat(bVal))) {
				aVal = parseFloat(aVal);
				bVal = parseFloat(bVal);
			} else if (typeof aVal === 'string' && typeof bVal === 'string') {
				aVal = aVal.toLowerCase();
				bVal = bVal.toLowerCase();
			}
			if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
			if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
			return 0;
		});
		return sorted;
	}, [loans, filter, sortKey, sortOrder, loanView, repledgeFilter]);

	// Calculate totals for footer
	const totalIssued = filteredLoans.reduce((sum, loan) => sum + toAmount(loan.issue_amount), 0);
	const totalCollected = filteredLoans.reduce((sum, loan) => sum + getLoanCollectedAmount(loan), 0);
	const totalBalance = filteredLoans.reduce((sum, loan) => sum + getLoanBalance(loan), 0);
	const totalAdjustments = filteredLoans.reduce((sum, loan) => sum + toAmount(loan.adjustments), 0);
	const customerOptions = customers.map(customer => ({
		value: customer.customer_id,
		label: `${customer.customer_id} - ${customer.customer_name}`,
	}));
	const selectedCustomerOption = customerOptions.find(option => option.value === newLoanForm.customer_id) || null;
	const loanTypeOptions = loanTypes.map(type => ({
		value: type.loan_type_name || type.loan_type_code,
		label: type.loan_type_name || type.loan_type_code,
	}));
	const selectedLoanTypeOption = loanTypeOptions.find(option => option.value === newLoanForm.loan_type) || null;
	const canCloseSelectedLoan = selectedLoan && isOpenLoan(selectedLoan);

	return (
		<div style={{ padding: 24 }}>
			<h2 style={{ color: 'navy', marginBottom: 20 }}>Loans</h2>
			<div className="mobile-toolbar" style={{ marginBottom: 16, fontSize: '13px', display: 'flex', alignItems: 'center', gap: 10 }}>
				<input
					type="text"
					placeholder="Filter loans..."
					value={filter}
					onChange={e => setFilter(e.target.value)}
					style={{ padding: 6, width: 240, fontSize: '13px' }}
				/>
				<button onClick={loadLoans} style={{ padding: '6px 18px', fontSize: '13px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4 }}>Refresh</button>
				<button type="button" onClick={openNewLoanModal} style={{ padding: '6px 18px', fontSize: '13px', background: 'navy', color: '#fff', border: 'none', borderRadius: 4 }}>New Loan</button>
				<button
					type="button"
					onClick={() => canCloseSelectedLoan && setShowCloseLoanModal(true)}
					disabled={!canCloseSelectedLoan}
					style={{
						padding: '6px 18px',
						fontSize: '13px',
						background: canCloseSelectedLoan ? '#d32f2f' : '#eee',
						color: canCloseSelectedLoan ? '#fff' : '#888',
						border: 'none',
						borderRadius: 4,
						cursor: canCloseSelectedLoan ? 'pointer' : 'not-allowed',
					}}
				>
					Close Loan
				</button>
				<select value={repledgeFilter} onChange={event => setRepledgeFilter(event.target.value)} style={{ padding: '6px 10px', fontSize: '13px', border: '1px solid #cfd8e3', borderRadius: 4 }}>
					<option value="all">All Re-Pledge Statuses</option>
					<option value="Not Pledged">Gold: Not Pledged</option>
					<option value="Pledged">Gold: Pledged</option>
					<option value="Overdue">Gold: Overdue</option>
					<option value="Released">Gold: Released</option>
				</select>
				<button
					type="button"
					onClick={toggleLoanView}
					style={{
						padding: '6px 18px',
						fontSize: '13px',
						background: loanView === 'all' ? '#1976d2' : loanView === 'open' ? '#388e3c' : '#d32f2f',
						color: '#fff',
						border: 'none',
						borderRadius: 4,
						marginLeft: 8,
						minWidth: 120,
						fontWeight: 600,
						boxShadow: loanView === 'all' ? '0 2px 8px #1976d233' : loanView === 'open' ? '0 2px 8px #388e3c33' : '0 2px 8px #d32f2f33',
						transition: 'background 0.2s',
					}}
				>
					{loanView === 'all' ? 'All Loans' : loanView === 'open' ? 'Open Loans' : 'Closed Loans'}
				</button>
			</div>
			<div
				className="loan-totals-strip"
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(4, minmax(130px, 1fr))',
					gap: 8,
					marginBottom: 12,
					fontSize: '13px',
				}}
			>
				<div style={{ background: '#f5f7fb', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
					<div style={{ color: '#667085', fontSize: '12px' }}>Loans</div>
					<div style={{ fontWeight: 700 }}>{filteredLoans.length.toLocaleString()}</div>
				</div>
				<div style={{ background: '#f5f7fb', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
					<div style={{ color: '#667085', fontSize: '12px' }}>Issued</div>
					<div style={{ fontWeight: 700 }}>{formatAmount(totalIssued)}</div>
				</div>
				<div style={{ background: '#f5f7fb', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
					<div style={{ color: '#667085', fontSize: '12px' }}>Collected</div>
					<div style={{ fontWeight: 700 }}>{formatAmount(totalCollected)}</div>
				</div>
				<div style={{ background: '#f5f7fb', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
					<div style={{ color: '#667085', fontSize: '12px' }}>Balance</div>
					<div style={{ fontWeight: 700 }}>{formatAmount(totalBalance)}</div>
				</div>
			</div>
			{showCloseLoanModal && (
				<CloseLoanForm
					loan={{
						...selectedLoan,
						collected_amount: getLoanCollectedAmount(selectedLoan),
					}}
					onClose={() => setShowCloseLoanModal(false)}
					onSuccess={() => {
						setShowCloseLoanModal(false);
						setSelectedLoan(null);
						loadLoans();
					}}
				/>
			)}
			{showNewLoanModal && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					width: '100vw',
					height: '100vh',
					background: 'rgba(0,0,0,0.25)',
					zIndex: 1000,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}>
					<form className="mobile-modal-panel" onSubmit={handleNewLoanSubmit} style={{
						background: '#fff',
						borderRadius: 8,
						boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
						padding: 28,
						minWidth: 360,
						maxWidth: 520,
						width: '100%',
					}}>
						<h3 style={{ marginTop: 0, color: 'navy' }}>New Loan</h3>
						{newLoanLoading ? (
							<div style={{ padding: '16px 0' }}>Loading...</div>
						) : (
							<>
								<div style={{ marginBottom: 10 }}>
									<label style={{ display: 'block', marginBottom: 4 }}>Customer</label>
									<Select
										options={customerOptions}
										value={selectedCustomerOption}
										onChange={option => {
											setNewLoanForm(current => ({
												...current,
												customer_id: option ? option.value : '',
											}));
										}}
										placeholder="Type code or name to search"
										isClearable
										styles={{ container: base => ({ ...base, width: '100%' }) }}
									/>
								</div>
								<div style={{ marginBottom: 10 }}>
									<label style={{ display: 'block', marginBottom: 4 }}>Loan Type</label>
									<Select
										options={loanTypeOptions}
										value={selectedLoanTypeOption}
										onChange={option => {
											setNewLoanForm(current => ({
												...current,
												loan_type: option ? option.value : '',
												maturity_date: option ? current.maturity_date : '',
											}));
										}}
										placeholder="Select Loan Type"
										isClearable
										styles={{ container: base => ({ ...base, width: '100%' }) }}
									/>
								</div>
								<div style={{ marginBottom: 10 }}>
									<label style={{ display: 'block', marginBottom: 4 }}>Issue Date</label>
									<input
										name="issue_date"
										type="date"
										value={newLoanForm.issue_date}
										onChange={handleNewLoanChange}
										required
										style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
									/>
								</div>
								<div style={{ marginBottom: 10 }}>
									<label style={{ display: 'block', marginBottom: 4 }}>Maturity Date</label>
									<input
										name="maturity_date"
										type="date"
										value={newLoanForm.maturity_date}
										readOnly
										style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box', background: '#f5f5f5' }}
									/>
								</div>
								<div style={{ marginBottom: 10 }}>
									<label style={{ display: 'block', marginBottom: 4 }}>Loan Amount</label>
									<input
										name="issue_amount"
										type="number"
										min="0"
										step="0.01"
										value={newLoanForm.issue_amount}
										onChange={handleNewLoanChange}
										required
										style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
									/>
								</div>
							</>
						)}
						{newLoanError && <div style={{ color: 'red', marginBottom: 12 }}>{newLoanError}</div>}
						<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 22 }}>
							<button type="button" onClick={closeNewLoanModal} disabled={newLoanSubmitting} style={{ padding: '8px 18px', borderRadius: 4, border: '1px solid #ccc', background: '#f5f5f5' }}>Cancel</button>
							<button type="submit" disabled={newLoanSubmitting || newLoanLoading} style={{ padding: '8px 18px', borderRadius: 4, border: 'none', background: 'navy', color: '#fff', fontWeight: 600 }}>
								{newLoanSubmitting ? 'Saving...' : 'Save'}
							</button>
						</div>
					</form>
				</div>
			)}
			<div className="desktop-table-wrap">
				<table className="fixed-header-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
					<thead>
						<tr style={{ position: 'sticky', top: 0, background: '#fafbfc', zIndex: 10 }}>
							{columns.map(col => (
								<th
									key={col.key}
									style={{ borderBottom: '1px solid #ccc', padding: '4px 6px', textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
									onClick={() => handleSort(col.key)}
								>
									{col.label}
									{sortKey === col.key && (
										<span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
									)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr><td colSpan={columns.length}>Loading...</td></tr>
						) : filteredLoans.length === 0 ? (
							<tr><td colSpan={columns.length}>No loans found.</td></tr>
						) : (
							filteredLoans.map((loan, idx) => (
								<tr
									key={loan.loan_id || idx}
									className={selectedLoan?.loan_id === loan.loan_id ? 'selected-record-row' : undefined}
									onClick={() => setSelectedLoan(loan)}
									style={{
										cursor: 'pointer',
										opacity: isOpenLoan(loan) ? 1 : 0.65,
									}}
								>
									{columns.map(col => {
										let value = loan[col.key];
										if (col.key === 'collected_amount') {
											value = formatAmount(getLoanCollectedAmount(loan));
										} else if (col.key === 'balance') {
											const balance = getLoanBalance(loan);
											value = formatAmount(balance);
										} else if (col.key === 'loan_type') {
											value = formatLoanType(value);
										} else if (col.key === 'repledge_status') {
											value = <RepledgeBadge loan={loan} />;
										} else if (col.key.toLowerCase().includes('date') && value) {
											value = value.split('T')[0];
											value = formatDate(value);
										}
										return (
											<td key={col.key} style={{ padding: '4px 6px', borderBottom: '1px solid #eee' }}>{value}</td>
										);
									})}
								</tr>
							))
						)}
					</tbody>
					<tfoot>
						<tr style={{ background: '#f9f9f9', fontWeight: 'bold', position: 'sticky', bottom: 0, zIndex: 2 }}>
							{columns.map((col, idx) => {
								if (col.key === 'issue_amount') {
									return (
										<td key={col.key} style={{ padding: '4px 6px', border: 'none', background: '#f9f9f9', textAlign: 'right' }}>
											Loan Issued: {totalIssued.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
										</td>
									);
								} else if (col.key === 'collected_amount') {
									return (
										<td key={col.key} style={{ padding: '4px 6px', border: 'none', background: '#f9f9f9', textAlign: 'right' }}>
											Collected: {totalCollected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
										</td>
									);
								} else if (col.key === 'balance') {
									return (
										<td key={col.key} style={{ padding: '4px 6px', border: 'none', background: '#f9f9f9', textAlign: 'right' }}>
											Balance: {totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
										</td>
									);
								} else if (col.key === 'adjustments') {
									return (
										<td key={col.key} style={{ padding: '4px 6px', border: 'none', background: '#f9f9f9', textAlign: 'right' }}>
											Adjustments: {totalAdjustments.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
										</td>
									);
								} else if (idx === 0) {
									return (
										<td key={col.key} style={{ padding: '4px 6px', border: 'none', background: '#f9f9f9', textAlign: 'right' }}>Totals:</td>
									);
								} else {
									return <td key={col.key} style={{ border: 'none', background: '#f9f9f9' }}></td>;
								}
							})}
						</tr>
					</tfoot>
				</table>
			</div>
			<div className="mobile-card-list">
				{loading ? (
					<div className="mobile-record-card">Loading...</div>
				) : filteredLoans.length === 0 ? (
					<div className="mobile-record-card">No loans found.</div>
				) : filteredLoans.map((loan, idx) => {
					const collected = getLoanCollectedAmount(loan);
					const issued = toAmount(loan.issue_amount);
					const balance = getLoanBalance(loan);
					const open = isOpenLoan(loan);

					return (
						<div
							key={loan.loan_id || idx}
							className={`mobile-record-card ${selectedLoan?.loan_id === loan.loan_id ? 'selected' : ''}`}
							onClick={() => setSelectedLoan(loan)}
						>
							<div className="mobile-card-title">
								<div>
									{loan.customer_name || 'Loan'}
									<div className="mobile-card-subtitle">Loan {loan.loan_id} · Customer {loan.customer_id}</div>
								</div>
								<span className="mobile-badge">{open ? 'Open' : 'Closed'}</span>
							</div>
							<div className="mobile-card-grid">
								<div className="mobile-card-field">
									<span className="mobile-card-label">Loan Type</span>
									<span className="mobile-card-value">{formatLoanType(loan.loan_type)}</span>
								</div>
								<div className="mobile-card-field">
									<span className="mobile-card-label">Issue Date</span>
									<span className="mobile-card-value">{loan.issue_date ? formatDate(String(loan.issue_date).split('T')[0]) : ''}</span>
								</div>
								<div className="mobile-card-field">
									<span className="mobile-card-label">Issued</span>
									<span className="mobile-card-value">{issued.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
								</div>
								<div className="mobile-card-field">
									<span className="mobile-card-label">Collected</span>
									<span className="mobile-card-value">{collected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
								</div>
								<div className="mobile-card-field full">
									<span className="mobile-card-label">Balance</span>
									<span className="mobile-card-value">{formatAmount(balance)}</span>
								</div>
								{isGoldLoan(loan) && (
									<div className="mobile-card-field full">
										<span className="mobile-card-label">Re-Pledge Status</span>
										<span className="mobile-card-value"><RepledgeBadge loan={loan} /></span>
									</div>
								)}
							</div>
						</div>
					);
				})}
				<div className="mobile-record-card">
					<div className="mobile-card-title">
						<div>Totals</div>
						<span>{filteredLoans.length.toLocaleString()} loans</span>
					</div>
					<div className="mobile-card-grid">
						<div className="mobile-card-field">
							<span className="mobile-card-label">Issued</span>
							<span className="mobile-card-value">{totalIssued.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
						</div>
						<div className="mobile-card-field">
							<span className="mobile-card-label">Collected</span>
							<span className="mobile-card-value">{totalCollected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
						</div>
						<div className="mobile-card-field full">
							<span className="mobile-card-label">Balance</span>
							<span className="mobile-card-value">{totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Loans;
